import { useMutation } from '@apollo/client';
import { CREATE_BOOKING_SECURE, CONFIRM_BOOKING_PAYMENT } from '../graphql/queries/bookingQueries';
import {
  describeApolloErrorForLog,
  describeBookingInputForLog,
  describeBookingResultForLog,
  describeMutationDataForLog,
  describeReservationForLog,
  logBookingDiagnostic,
} from '../util/bookingDiagnostics';

// Builds the four fields the backend integrity check compares between
// Phase 1 (createBookingSecure) and Phase 3 (confirmBookingPayment):
// creativePackages, bookedEquipment, numberOfGuests, packageId.
// The Lambda's assertReservationIntegrity rebuilds a signature from these and
// rejects with "Die Buchungsdaten wurden nach der Autorisierung verändert."
// if Phase 3 sends anything that normalizes differently from Phase 1.
const buildReservationFields = (bookingData, studioCreativesData) => {
  const {
    creativePackages,
    bookedEquipment,
    packageId,
    packageName,
    packageDurationHours,
    numberOfGuests,
  } = bookingData || {};

  const transformedCreativePackages = (creativePackages || []).map((pkg) => {
    const packageID = pkg.packageId || pkg.packageID;

    let resolvedPackageName = null;
    let resolvedPackageType = null;

    if (studioCreativesData && Array.isArray(studioCreativesData) && pkg.creativeId) {
      const studioCreative = studioCreativesData.find(
        (sc) => sc?.creative?.id === pkg.creativeId
      );

      if (studioCreative?.creative?.packages?.items) {
        const packageData = studioCreative.creative.packages.items.find(
          (p) => p?.id === packageID
        );

        if (packageData) {
          resolvedPackageName = packageData.name || null;
          resolvedPackageType = packageData.packageType || 'CUSTOM';
        }
      }
    }

    // resolvedPackageType: live lookup from studioCreativesData (most reliable).
    // pkg.packageType: persisted at creative-selection time in CreativeModal.jsx —
    //   serves as fallback when studioCreativesData has not yet resolved in Phase 3
    //   (e.g. cold-cache localStorage resume). Without this fallback, a BASIC creative
    //   would resolve to 'CUSTOM' → isHourly=false, mismatching the Phase 1 snapshot
    //   and causing an integrity-rejection AFTER Stripe authorisation.
    const finalPackageType = resolvedPackageType || pkg.packageType || 'CUSTOM';

    return {
      packageID,
      packageName: resolvedPackageName || `custom-${pkg.creativeId || 'package'}`,
      packageType: finalPackageType,
      price: 0,
      // The backend forces isHourly = (packageType === 'BASIC') in
      // createBookingSecure (`serverAllowsHourly`) and stores that value on
      // the reservation snapshot. confirmBookingPayment's integrity check
      // normalizes both the snapshot and our Phase 3 input via
      // `isHourly: pkg?.isHourly === true`, so we must mirror the same
      // value here. Otherwise the signature differs and the backend rejects
      // with "Buchungsdaten wurden nach der Autorisierung verändert".
      isHourly: finalPackageType === 'BASIC',
      specialRequests:
        pkg.specialRequests && typeof pkg.specialRequests === 'string' && pkg.specialRequests.trim() !== ''
          ? pkg.specialRequests.trim()
          : '',
    };
  }).filter((pkg) => pkg.packageID && typeof pkg.packageID === 'string' && pkg.packageID.trim() !== '');

  const transformedEquipment = (Array.isArray(bookedEquipment) ? bookedEquipment : [])
    .filter((eq) => eq && eq.equipmentItemId && eq.name && Number(eq.quantity) > 0)
    .map((eq) => ({
      equipmentItemId: eq.equipmentItemId,
      name: eq.name,
      quantity: parseInt(eq.quantity, 10) || 1,
    }));

  const fields = {};

  if (transformedCreativePackages.length > 0) {
    fields.creativePackages = transformedCreativePackages;
  }

  if (transformedEquipment.length > 0) {
    fields.bookedEquipment = transformedEquipment;
  }

  if (packageId && typeof packageId === 'string' && packageId.trim() !== '') {
    fields.packageId = packageId.trim();
    if (packageName && typeof packageName === 'string') {
      fields.packageName = packageName.trim();
    }
    if (packageDurationHours && !Number.isNaN(parseFloat(packageDurationHours))) {
      fields.packageDurationHours = parseFloat(packageDurationHours);
    }
  }

  if (numberOfGuests !== undefined && numberOfGuests !== null && !Number.isNaN(parseInt(numberOfGuests, 10))) {
    fields.numberOfGuests = parseInt(numberOfGuests, 10);
  }

  return fields;
};

export const useBookingStudio = () => {
  const [createBookingSecure, { loading: reservingSlot, error: reservationError }] = useMutation(CREATE_BOOKING_SECURE);
  const [confirmBookingPayment, { loading: confirmingBooking, error: confirmationError }] = useMutation(CONFIRM_BOOKING_PAYMENT);

  /**
   * PHASE 1: Reserve slot (Lock-First Pattern)
   * Creates a temporary reservation, NOT a booking!
   * Returns reservation/payment route data plus nullable server pricing fields.
   */
  const handleSlotReservation = async (bookingData, studioCreativesData = null) => {
    try {
      const studioID = bookingData.studioID || bookingData.studioId;

      if (!studioID) {
        throw new Error('Missing required field: studioID');
      }

      const {
        startDateTime,
        endDateTime,
        projectName,
        projectDescription,
        companyName,
      } = bookingData;

      // Validate required fields (check for null, undefined, or empty strings)
      // Note: GraphQL NonNull String! doesn't accept null, undefined, or empty strings
      if (!startDateTime || typeof startDateTime !== 'string' || startDateTime.trim() === '') {
        throw new Error(`Missing or invalid required field: startDateTime (value: ${startDateTime}, type: ${typeof startDateTime})`);
      }

      if (!endDateTime || typeof endDateTime !== 'string' || endDateTime.trim() === '') {
        throw new Error(`Missing or invalid required field: endDateTime (value: ${endDateTime}, type: ${typeof endDateTime})`);
      }

      if (!projectName || typeof projectName !== 'string' || projectName.trim() === '') {
        throw new Error(`Missing or invalid required field: projectName (value: ${projectName}, type: ${typeof projectName})`);
      }

      if (!projectDescription || typeof projectDescription !== 'string' || projectDescription.trim() === '') {
        throw new Error(`Missing or invalid required field: projectDescription (value: ${projectDescription}, type: ${typeof projectDescription})`);
      }

      const sanitizedProjectName = projectName.trim();
      const sanitizedProjectDescription = projectDescription.trim();
      const sanitizedStartDateTime = startDateTime.trim();
      const sanitizedEndDateTime = endDateTime.trim();
      const sanitizedStudioID = String(studioID).trim();

      // Final validation after trimming
      if (!sanitizedProjectName) {
        throw new Error('projectName cannot be empty after trimming');
      }
      if (!sanitizedProjectDescription) {
        throw new Error('projectDescription cannot be empty after trimming');
      }
      if (!sanitizedStartDateTime) {
        throw new Error('startDateTime cannot be empty');
      }
      if (!sanitizedEndDateTime) {
        throw new Error('endDateTime cannot be empty');
      }
      if (!sanitizedStudioID) {
        throw new Error('studioID cannot be empty');
      }

      const input = {
        studioID: sanitizedStudioID,
        startDateTime: sanitizedStartDateTime,
        endDateTime: sanitizedEndDateTime,
        projectName: sanitizedProjectName,
        projectDescription: sanitizedProjectDescription,
        ...buildReservationFields(bookingData, studioCreativesData),
      };

      if (companyName && typeof companyName === 'string' && companyName.trim()) {
        input.companyName = companyName.trim();
      }

      let mutationResult;
      try {
        mutationResult = await createBookingSecure({ variables: { input } });
      } catch (mutationError) {
        logBookingDiagnostic('[useBookingStudio]', 'Phase 1 backend rejected createBookingSecure', {
          input: describeBookingInputForLog(input),
          backendError: describeApolloErrorForLog(mutationError),
        });
        throw mutationError;
      }

      const { data } = mutationResult;

      if (!data || !data.createBookingSecure) {
        logBookingDiagnostic('[useBookingStudio]', 'Phase 1 backend returned no createBookingSecure payload', {
          input: describeBookingInputForLog(input),
          data: describeMutationDataForLog(data),
        });
        throw new Error('Invalid response from booking server');
      }

      const reservation = data.createBookingSecure;

      if (!reservation.slotID || !reservation.reservationToken || !reservation.clientSecret || !reservation.paymentIntentID) {
        logBookingDiagnostic('[useBookingStudio]', 'Phase 1 backend returned incomplete reservation', {
          input: describeBookingInputForLog(input),
          reservation: describeReservationForLog(reservation),
        });
        throw new Error('Reservation konnte nicht erstellt werden. Bitte versuche es erneut.');
      }

      return {
        slotID: reservation.slotID,
        reservationToken: reservation.reservationToken,
        clientSecret: reservation.clientSecret,
        paymentIntentID: reservation.paymentIntentID,
        expiresAt: reservation.expiresAt,
        usesConnect: reservation.usesConnect,
        stripeConnectAccountId: reservation.stripeConnectAccountId || null,
        payoutMode: reservation.payoutMode,
        totalAmountCents: reservation.totalAmountCents ?? null,
        originalAmountCents: reservation.originalAmountCents ?? null,
        campaignDiscountAmountCents: reservation.campaignDiscountAmountCents ?? null,
        totalPrice: reservation.totalPrice ?? null,
        originalTotalPrice: reservation.originalTotalPrice ?? null,
        campaignDiscountAmount: reservation.campaignDiscountAmount ?? null,
        campaignApplied: reservation.campaignApplied ?? null,
      };
    } catch (error) {
      logBookingDiagnostic('[useBookingStudio]', 'handleSlotReservation threw', {
        error: describeApolloErrorForLog(error),
      });
      throw error;
    }
  };

  /**
   * PHASE 3: Create booking after successful payment
   * Uses reservation data from Phase 1 to create the actual booking
   * Returns: { booking, success }
   */
  const handleBookingConfirmation = async (reservationData, bookingData, studioCreativesData = null) => {
    try {
      const { projectName, projectDescription, companyName } = bookingData;

      // Phase 3 input. slotID + reservationToken authenticate the request.
      // The backend's assertReservationIntegrity compares creativePackages,
      // bookedEquipment, numberOfGuests and packageId from this input against
      // the reservation snapshot stored in Phase 1; mismatches fail with
      // "Die Buchungsdaten wurden nach der Autorisierung verändert."
      // We rebuild those four fields with the exact same helper used in
      // Phase 1 so the signatures match.
      const input = {
        slotID: reservationData.slotID,
        reservationToken: reservationData.reservationToken,
        paymentIntentID: reservationData.paymentIntentID,
        projectName: projectName.trim(),
        projectDescription: projectDescription.trim(),
        ...buildReservationFields(bookingData, studioCreativesData),
      };

      if (companyName && typeof companyName === 'string' && companyName.trim()) {
        input.companyName = companyName.trim();
      }

      let mutationResult;
      try {
        mutationResult = await confirmBookingPayment({ variables: { input } });
      } catch (mutationError) {
        logBookingDiagnostic('[useBookingStudio]', 'Phase 3 backend rejected confirmBookingPayment', {
          input: describeBookingInputForLog(input),
          backendError: describeApolloErrorForLog(mutationError),
        });
        throw mutationError;
      }

      const { data } = mutationResult;

      if (!data || !data.confirmBookingPayment) {
        logBookingDiagnostic('[useBookingStudio]', 'Phase 3 backend returned no confirmBookingPayment payload', {
          input: describeBookingInputForLog(input),
          data: describeMutationDataForLog(data),
        });
        throw new Error('Invalid response from booking confirmation server');
      }

      const result = data.confirmBookingPayment;

      if (!result.success || !result.booking) {
        logBookingDiagnostic('[useBookingStudio]', 'Phase 3 backend returned unsuccessful confirmation', {
          input: describeBookingInputForLog(input),
          result: describeBookingResultForLog(result),
        });
        throw new Error('Booking confirmation failed');
      }

      return {
        booking: result.booking,
        success: result.success
      };
    } catch (error) {
      logBookingDiagnostic('[useBookingStudio]', 'handleBookingConfirmation threw', {
        error: describeApolloErrorForLog(error),
      });
      throw error;
    }
  };

  return {
    handleSlotReservation,
    handleBookingConfirmation,
    loading: reservingSlot || confirmingBooking,
    error: reservationError || confirmationError
  };
};
